import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { AnimatePresence, motion } from 'framer-motion'
import posthog from 'posthog-js'
import { UNSEEN_COUNT_KEY, badgeText, useUnseenCount } from 'modules/inbox'
import { faPlain } from 'utilities/helper/format'
interface IFooterItemsProps {
    icon: string | undefined
    isActive: boolean
    title: string
    router: string
    role: string
    activeIcon: string
}

const Items = ({
    icon,
    isActive,
    title,
    router,
    role,
    activeIcon,
}: IFooterItemsProps) => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const isMessages = role === 'Messages'

    const onClick = () => {
        if (isMessages)
            posthog.capture('footer_messages_clicked', {
                unread_count:
                    queryClient.getQueryData<number>(UNSEEN_COUNT_KEY) ?? null,
                from_path: window.location.pathname,
            })
        navigate(router)
    }

    return (
        <div
            onClick={onClick}
            className="m-auto flex flex-col items-center text-center cursor-pointer select-none active:scale-95"
        >
            <div
                className={classNames(
                    'relative flex items-center justify-center w-12 h-8 rounded-2xl transition-colors duration-200',
                    isActive ? 'bg-green-2' : 'bg-transparent'
                )}
            >
                <img
                    src={isActive ? activeIcon : icon}
                    alt={title}
                    className="w-6 h-6"
                />
                {isMessages && <UnseenBadge />}
            </div>
            <small
                className={classNames(
                    'mt-1 text-center text-[11px]',
                    isActive
                        ? 'text-green-1 font-bold'
                        : 'text-gray-2 font-semibold'
                )}
            >
                {title}
            </small>
        </div>
    )
}

/**
 * Unread messages on the «پیام‌ها» tab. It pops in with a spring when a new
 * one arrives and shrinks away once they are read — here or on another device.
 */
function UnseenBadge() {
    const count = useUnseenCount()
    return (
        <AnimatePresence>
            {count > 0 && (
                <motion.span
                    key="badge"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 520, damping: 24 }}
                    className="absolute -top-1.5 left-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
                    aria-label={`${faPlain.format(count)} پیام خوانده‌نشده`}
                >
                    <motion.span
                        key={count}
                        initial={{ y: -6, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        {badgeText(count, (n) => faPlain.format(n))}
                    </motion.span>
                </motion.span>
            )}
        </AnimatePresence>
    )
}

export default Items
